import { PlanTier } from '@fluenceur/types';
import { paywallScopeKey } from '@/libs/paywall-url-state';
import { useUpgradeModalStore } from '@/stores/upgrade-modal-store';
import { useCallback, useEffect, useRef } from 'react';
import { usePaywall } from './use-paywall';

/** Bridge imperative API error intentions to nuqs, keeping refusal data in its scope. */
export function usePaywallHost() {
  const paywall = usePaywall();
  const {
    scopeKey,
    origin,
    closePaywall,
    openUpgradeModal,
    openPlanLimitModal,
    openInferredPlanLimitModal,
  } = paywall;
  const request = useUpgradeModalStore((state) => state.request);
  const context = useUpgradeModalStore((state) => state.context);
  const previousScope = useRef(scopeKey);
  const previousBrand = useRef(paywall.scope.brandId);
  const scopeChanged = !!previousBrand.current && previousScope.current !== scopeKey;
  const matchesContext =
    context?.paywall === paywall.paywall &&
    context.origin === origin &&
    paywallScopeKey(context.scope) === scopeKey;

  useEffect(
    () => () => {
      const state = useUpgradeModalStore.getState();
      if (state.context?.origin === origin && paywallScopeKey(state.context.scope) === scopeKey) {
        state.closeUpgradeModal();
      }
    },
    [origin, scopeKey]
  );

  useEffect(() => {
    const shouldClose = scopeChanged;
    previousScope.current = scopeKey;
    previousBrand.current = paywall.scope.brandId;
    if (!shouldClose) return;
    closePaywall({ notify: false, history: 'replace' });
  }, [scopeChanged, scopeKey, paywall.scope.brandId, closePaywall]);

  useEffect(() => {
    if (!request || scopeChanged) return;
    useUpgradeModalStore.getState().consumeRequest(request.id);
    const sameScope = request.scope !== null && paywallScopeKey(request.scope) === scopeKey;
    if (request.scope && !sameScope) {
      useUpgradeModalStore.getState().closeUpgradeModal();
      return;
    }
    if (request.paywall === 'plans')
      openUpgradeModal('generic', request.plan === 'growth' ? PlanTier.GROWTH : PlanTier.ESSENTIAL);
    else if (sameScope && request.planLimit) openPlanLimitModal(request.planLimit);
    else openInferredPlanLimitModal(request.paywall);
  }, [
    request,
    scopeChanged,
    scopeKey,
    openUpgradeModal,
    openPlanLimitModal,
    openInferredPlanLimitModal,
  ]);

  useEffect(() => {
    if (context && !matchesContext && !request) {
      useUpgradeModalStore.getState().closeUpgradeModal();
    }
  }, [context, matchesContext, request]);

  const close = useCallback(() => closePaywall(), [closePaywall]);

  return {
    paywall: scopeChanged ? null : paywall.paywall,
    minimumPlan: paywall.minimumPlan,
    planLimit: matchesContext && !scopeChanged ? (context?.planLimit ?? null) : null,
    closePaywall: close,
  };
}
